<template>
  <div class="report-designer">
    <header class="designer-toolbar">
      <div class="designer-toolbar__identity">
        <hs-button
          icon="arrow-left"
          icon-only
          variant="ghost"
          title="Back to reports"
          @click="goBack"
        />
        <div>
          <p class="toolbar-label">Custom report</p>
          <input
            v-model.trim="reportName"
            class="report-name"
            aria-label="Report name"
            maxlength="80"
            :disabled="isPublished"
          />
        </div>
      </div>
      <div class="designer-toolbar__actions">
        <hs-badge :tone="isPublished ? 'success' : 'warning'">
          {{ isPublished ? "Published" : "Draft" }}
        </hs-badge>
        <span v-if="saveStatus" class="save-status" :class="`is-${saveStatusTone}`">
          {{ saveStatus }}
        </span>
        <hs-button
          icon="database"
          variant="secondary"
          :loading="isPreviewing"
          :disabled="isSaving || isPreviewing || !capabilities.report_definitions"
          @click="previewDefinition"
        >
          Preview data
        </hs-button>
        <hs-button
          icon="download"
          variant="secondary"
          :loading="isExporting"
          :disabled="isSaving || isPreviewing || isExporting"
          @click="downloadReport('XLSX')"
        >
          Excel
        </hs-button>
        <hs-button
          icon="printer"
          variant="secondary"
          :loading="isExporting"
          :disabled="isSaving || isPreviewing || isExporting"
          @click="downloadReport('PDF')"
        >
          PDF
        </hs-button>
        <hs-button
          icon="save"
          :loading="isSaving"
          :disabled="!canSave"
          @click="saveDefinition"
        >
          Save
        </hs-button>
        <hs-button
          v-if="!isPublished && reportScopeMode === 'SINGLE'"
          icon="send"
          variant="success"
          :loading="isPublishing"
          :disabled="isSaving || isPublishing || !definitionId"
          @click="showPublishDialog = true"
        >
          Publish
        </hs-button>
        <hs-button
          v-if="isPublished"
          icon="edit-3"
          variant="secondary"
          :loading="isCreatingDraft"
          @click="showDraftDialog = true"
        >
          New draft
        </hs-button>
        <hs-button
          v-if="isPublished"
          icon="share-2"
          variant="success"
          :loading="isSharing"
          @click="sharePublishedTemplate"
        >
          Share template
        </hs-button>
      </div>
    </header>

    <div v-if="loadError" class="designer-error" role="alert">
      <hs-icon name="alert-triangle" tone="danger" />
      <div>
        <strong>Datasets unavailable</strong>
        <p>{{ loadError }}</p>
      </div>
      <hs-button size="sm" variant="secondary" @click="fetchDatasets">
        Retry
      </hs-button>
    </div>

    <section class="designer-scope" aria-labelledby="designer-scope-title">
      <div>
        <p class="panel-label">Report context</p>
        <h2 id="designer-scope-title">Scope and entity resolution</h2>
      </div>
      <label>
        <span>Scope</span>
        <select v-model="reportScopeMode" :disabled="isPublished" @change="handleScopeModeChange">
          <option value="SINGLE">Current store</option>
          <option value="MULTI">Multiple authorized stores</option>
        </select>
      </label>
      <label>
        <span>Product identity</span>
        <select v-model="reportEntityJoin" :disabled="isPublished || reportScopeMode !== 'MULTI'">
          <option value="">Store-local identity</option>
          <option value="SKU">SKU (recommended)</option>
          <option value="NAME">Product name</option>
          <option value="BARCODE">Barcode</option>
        </select>
      </label>
      <insights-store-selector
        v-if="reportScopeMode === 'MULTI'"
        v-model="reportScopeStoreIds"
        :stores="insightsStores"
        :groups="insightsGroups"
        :max-stores="insightsLimits.maxStores || 0"
        @apply-group="applyInsightsGroup"
      />
      <p v-if="reportScopeMode === 'MULTI' && reportEntityJoin" class="designer-scope__warning">
        Entity-resolution metadata is saved with the shared report document.
        Explore currently executes at store-local identity grain and reports this explicitly.
      </p>
    </section>

    <div class="designer-workspace">
      <aside class="dataset-panel">
        <div class="panel-heading">
          <div>
            <p class="panel-label">Data</p>
            <h2>Business data</h2>
          </div>
          <hs-icon v-if="isLoading" name="loader" spin tone="primary" />
        </div>

        <label class="data-search">
          <hs-icon name="search" :size="15" />
          <input
            v-model.trim="dataSearch"
            type="search"
            placeholder="Find sales, stock, expenses..."
            aria-label="Search business data"
          />
        </label>

        <p v-if="!isLoading && !datasets.length && !loadError" class="empty-copy">
          No datasets are available for your permissions.
        </p>

        <section
          v-for="group in datasetGroups"
          :key="group.category"
          class="dataset-group"
        >
          <p class="dataset-group__label">{{ group.category }}</p>
          <button
            v-for="dataset in group.datasets"
            :key="dataset.id"
            class="dataset-option"
            type="button"
            :disabled="isPublished"
            @click="openDatasetPicker(dataset)"
          >
            <hs-icon :name="datasetIcon(dataset.id)" :size="17" />
            <span>
              <strong>{{ dataset.label }}</strong>
              <small>
                {{ systemFieldCount(dataset) }} ready columns
                <template v-if="unavailableFieldCount(dataset)">
                  · {{ unavailableFieldCount(dataset) }} planned
                </template>
              </small>
            </span>
            <hs-icon name="chevron-right" :size="16" />
          </button>
        </section>
      </aside>

      <main class="sheet-stage">
        <div class="sheet-zoom-surface" :style="sheetZoomStyle">
        <div class="sheet-viewport" :style="sheetViewportStyle">
          <div class="sheet-corner"></div>
          <div class="column-headings" :style="columnHeadingsStyle">
            <span v-for="column in columnHeadings" :key="column">
              {{ column }}
            </span>
          </div>
          <div class="row-headings" :style="rowHeadingsStyle">
            <span v-for="row in sheetRowCount" :key="row">{{ row }}</span>
          </div>

          <section
            class="virtual-sheet"
            :style="virtualSheetStyle"
            aria-label="Report workbook sheet"
            @click="clearSelection"
          >
            <div
              v-for="block in activeSheet.blocks"
              :key="block.id"
              class="report-table-block"
              :class="{
                'is-selected': block.id === selectedBlockId,
                'has-collision': collidingBlockIds.includes(block.id),
              }"
              :data-block-id="block.id"
              :style="blockStyle(block)"
              tabindex="0"
              @click.stop="selectBlock(block.id)"
              @focus="selectBlock(block.id)"
            >
              <div class="report-table-block__title" title="Drag to move table">
                <hs-icon name="move" :size="15" />
                <strong>{{ block.name }}</strong>
                <span>{{ formatGrain(block.grain) }}</span>
              </div>
              <div
                v-if="blockFieldGuide(block).length"
                class="report-field-guide"
              >
                <strong>How calculated</strong>
                <dl>
                  <div
                    v-for="entry in blockFieldGuide(block)"
                    :key="entry.id"
                  >
                    <dt>{{ entry.label }}</dt>
                    <dd>
                      <span v-if="entry.meaning">
                        <strong>Meaning:</strong> {{ entry.meaning }}
                      </span>
                      <span v-if="entry.calculation">
                        <strong>Formula:</strong> {{ entry.calculation }}
                      </span>
                      <span v-if="entry.rollup">
                        <strong>Current rollup:</strong> {{ entry.rollup }}
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>
              <table>
                <thead>
                  <tr>
                    <th v-for="field in block.fields" :key="field.id">
                      {{ field.label }}
                    </th>
                  </tr>
                </thead>
                <tbody v-if="displayedRows(block).length">
                  <tr
                    v-for="(row, rowIndex) in displayedRows(block)"
                    :key="rowIndex"
                  >
                    <td v-for="field in block.fields" :key="field.id">
                      {{ displayCellValue(block, field, row, rowIndex) }}
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td :colspan="block.fields.length" class="empty-table-row">
                      No matching data
                    </td>
                  </tr>
                </tbody>
              </table>
              <div v-if="block.previewRows" class="preview-row-status">
                {{ block.previewRows.length }} live rows
                <span v-if="block.previewHasMore">· more available</span>
              </div>
            </div>

            <button
              v-if="!activeSheet.blocks.length"
              class="sheet-empty"
              type="button"
              :disabled="!datasets.length"
              @click="datasets[0] && openDatasetPicker(datasets[0])"
            >
              <hs-icon name="plus-square" tone="primary" soft />
              <strong>Add your first table</strong>
              <span>Select a table from the data panel</span>
            </button>
          </section>
        </div>
        </div>
      </main>

      <aside class="properties-panel">
        <template v-if="selectedBlock">
          <div class="properties-heading">
            <div>
              <p class="panel-label">Selected table</p>
              <h2>{{ selectedBlock.name }}</h2>
            </div>
            <button
              type="button"
              class="icon-action icon-action--danger"
              title="Remove table"
              @click="removeSelectedBlock"
            >
              <hs-icon name="trash-2" :size="16" />
            </button>
          </div>

          <label class="property-field">
            <span>Table name</span>
            <input
              v-model.trim="selectedBlock.name"
              maxlength="80"
              :disabled="isPublished"
            />
          </label>

          <div class="position-summary">
            <span>Position</span>
            <strong>
              {{ cellReference(selectedBlock.column, selectedBlock.row) }}
            </strong>
          </div>

          <label class="property-field">
            <span>Placement</span>
            <select
              v-model="selectedBlock.layoutMode"
              :disabled="isPublished"
              @change="handleLayoutModeChange"
            >
              <option value="FLOW">Smart flow</option>
              <option value="FIXED">Exact position</option>
            </select>
          </label>
          <p v-if="selectedBlock.layoutMode === 'FIXED'" class="placement-note">
            Exact-position tables cannot overlap another exact-position table.
          </p>
          <p v-if="collidingBlockIds.length" class="placement-error" role="alert">
            Exact-position tables overlap. Move one table before saving.
          </p>

          <section class="query-editor">
            <div class="query-editor__heading">
              <div>
                <span>Data rules</span>
                <small>Applied before preview and export</small>
              </div>
              <hs-icon name="sliders" :size="15" />
            </div>

            <div
              v-if="selectedDataset?.scheduled_period_modes?.length"
              class="query-section"
            >
              <label class="property-field query-period">
                <span>Scheduled delivery rows</span>
                <select
                  v-model="selectedBlock.query.scheduled_period_mode"
                  :disabled="isPublished"
                >
                  <option
                    v-for="mode in selectedDataset.scheduled_period_modes"
                    :key="mode.id"
                    :value="mode.id"
                  >
                    {{ mode.label }}
                  </option>
                </select>
              </label>
              <small class="query-empty">
                Applied when a daily, weekly, or monthly subscription runs.
                Interactive previews keep the date range below.
              </small>
            </div>

            <div v-if="selectedDateField" class="query-section">
              <label class="query-toggle">
                <input
                  type="checkbox"
                  :checked="Boolean(selectedBlock.query.date_range)"
                  :disabled="isPublished"
                  @change="toggleDateRange"
                />
                <span>
                  <strong>Date range</strong>
                  <small>{{ selectedDateField.label }} · store local time</small>
                </span>
              </label>
              <div
                v-if="selectedBlock.query.date_range"
                class="query-date-range"
              >
                <label>
                  <span>From</span>
                  <input
                    v-model="selectedBlock.query.date_range.start_date"
                    type="date"
                    :disabled="isPublished"
                  />
                </label>
                <label>
                  <span>Through</span>
                  <input
                    v-model="selectedBlock.query.date_range.end_date"
                    type="date"
                    :disabled="isPublished"
                  />
                </label>
              </div>
            </div>

            <div class="query-section">
              <div class="query-section__head">
                <span>Filters</span>
                <button
                  type="button"
                  :disabled="isPublished || !selectedDatasetFields.length"
                  @click="addQueryFilter"
                >
                  <hs-icon name="plus" :size="12" /> Add
                </button>
              </div>
              <div
                v-for="(filter, index) in selectedBlock.query.filters"
                :key="`filter-${index}`"
                class="query-rule"
              >
                <select
                  v-model="filter.field_id"
                  :disabled="isPublished"
                  @change="resetFilterOperator(filter)"
                >
                  <option
                    v-for="field in filterableFields"
                    :key="field.id"
                    :value="field.id"
                  >
                    {{ field.label }}
                  </option>
                </select>
                <select v-model="filter.operator" :disabled="isPublished">
                  <option
                    v-for="operator in filterOperators(filter.field_id)"
                    :key="operator"
                    :value="operator"
                  >
                    {{ formatOperator(operator) }}
                  </option>
                </select>
                <input
                  v-if="!isValuelessOperator(filter.operator)"
                  v-model="filter.value"
                  :type="filterInputType(filter.field_id)"
                  :placeholder="filter.operator === 'IN' || filter.operator === 'NOT_IN' ? 'Comma-separated values' : 'Value'"
                  :disabled="isPublished"
                />
                <input
                  v-if="filter.operator === 'BETWEEN'"
                  v-model="filter.value_to"
                  :type="filterInputType(filter.field_id)"
                  placeholder="And"
                  :disabled="isPublished"
                />
                <button
                  type="button"
                  title="Remove filter"
                  :disabled="isPublished"
                  @click="selectedBlock.query.filters.splice(index, 1)"
                >
                  <hs-icon name="x" :size="13" />
                </button>
              </div>
              <small v-if="!selectedBlock.query.filters.length" class="query-empty">
                All matching rows are included.
              </small>
            </div>

            <div v-if="selectedDateField" class="query-section">
              <label class="property-field query-period">
                <span>Aggregate by period</span>
                <select
                  :value="selectedBlock.query.grouping?.period || 'NONE'"
                  :disabled="isPublished"
                  @change="setGroupingPeriod($event.target.value)"
                >
                  <option value="NONE">No aggregation</option>
                  <option value="DAY">Daily</option>
                  <option value="WEEK">Weekly</option>
                  <option value="MONTH">Monthly</option>
                </select>
              </label>
              <div v-if="selectedBlock.query.grouping" class="aggregation-list">
                <div
                  v-for="aggregation in selectedBlock.query.aggregations"
                  :key="aggregation.field_id"
                  class="aggregation-rule"
                >
                  <span>{{ fieldLabel(aggregation.field_id) }}</span>
                  <select
                    v-model="aggregation.function"
                    :disabled="isPublished"
                  >
                    <option
                      v-for="option in aggregationOptions(aggregation.field_id)"
                      :key="option"
                      :value="option"
                    >
                      {{ formatOperator(option) }}
                    </option>
                  </select>
                </div>
                <small class="query-empty">
                  Date is bucketed by {{ selectedBlock.query.grouping.period.toLowerCase() }};
                  selected dimensions are grouped automatically.
                </small>
              </div>
            </div>

            <div class="query-section">
              <div class="query-section__head">
                <span>Sort</span>
                <button
                  type="button"
                  :disabled="isPublished || selectedBlock.query.sort.length >= 5"
                  @click="addQuerySort"
                >
                  <hs-icon name="plus" :size="12" /> Add
                </button>
              </div>
              <div
                v-for="(sort, index) in selectedBlock.query.sort"
                :key="`sort-${index}`"
                class="query-rule query-rule--sort"
              >
                <select v-model="sort.field_id" :disabled="isPublished">
                  <option
                    v-for="field in selectedSystemFields"
                    :key="field.id"
                    :value="field.id"
                  >
                    {{ field.label }}
                  </option>
                </select>
                <select v-model="sort.direction" :disabled="isPublished">
                  <option value="ASC">Ascending</option>
                  <option value="DESC">Descending</option>
                </select>
                <button
                  type="button"
                  title="Remove sort"
                  :disabled="isPublished"
                  @click="selectedBlock.query.sort.splice(index, 1)"
                >
                  <hs-icon name="x" :size="13" />
                </button>
              </div>
            </div>
          </section>

          <div class="column-editor">
            <div class="column-editor__heading">
              <span>Columns</span>
              <button
                type="button"
                class="add-manual-column"
                :disabled="isPublished"
                @click="openManualColumnDialog"
              >
                <hs-icon name="plus" :size="13" />
                Add blank column
              </button>
            </div>
            <div
              v-for="field in selectedDatasetFields"
              :key="field.id"
              class="column-option"
            >
              <label>
                <input
                  type="checkbox"
                  :checked="isFieldSelected(field.id)"
                  :disabled="isPublished || isOnlySelectedField(field.id)"
                  @change="toggleField(field)"
                />
                <span>{{ field.label }}</span>
              </label>
              <span v-if="isFieldSelected(field.id)" class="column-actions">
                <button
                  type="button"
                  title="Move column left"
                  :disabled="selectedFieldIndex(field.id) === 0"
                  @click="moveField(field.id, -1)"
                >
                  <hs-icon name="arrow-up" :size="13" />
                </button>
                <button
                  type="button"
                  title="Move column right"
                  :disabled="selectedFieldIndex(field.id) === selectedBlock.fields.length - 1"
                  @click="moveField(field.id, 1)"
                >
                  <hs-icon name="arrow-down" :size="13" />
                </button>
              </span>
            </div>
            <div
              v-for="field in selectedManualFields"
              :key="field.id"
              class="column-option column-option--manual"
            >
              <span class="manual-column-label">
                <hs-icon name="edit-3" :size="13" />
                <span>
                  <strong>{{ field.label }}</strong>
                  <small>Blank · {{ formatDataType(field.data_type) }}</small>
                </span>
              </span>
              <span class="column-actions">
                <button
                  type="button"
                  title="Move column left"
                  :disabled="selectedFieldIndex(field.id) === 0"
                  @click="moveField(field.id, -1)"
                >
                  <hs-icon name="arrow-up" :size="13" />
                </button>
                <button
                  type="button"
                  title="Move column right"
                  :disabled="selectedFieldIndex(field.id) === selectedBlock.fields.length - 1"
                  @click="moveField(field.id, 1)"
                >
                  <hs-icon name="arrow-down" :size="13" />
                </button>
                <button
                  type="button"
                  title="Remove blank column"
                  @click="removeManualField(field.id)"
                >
                  <hs-icon name="x" :size="13" />
                </button>
              </span>
            </div>
          </div>
        </template>

        <template v-else>
          <p class="panel-label">Workbook</p>
          <label class="property-field">
            <span>Sheet name</span>
            <input
              v-model.trim="activeSheet.name"
              maxlength="31"
              :disabled="isPublished"
            />
          </label>
          <div class="sheet-order-actions">
            <button
              type="button"
              :disabled="activeSheetIndex === 0"
              @click="moveSheet(-1)"
            >
              <hs-icon name="arrow-left" :size="14" />
              Move left
            </button>
            <button
              type="button"
              :disabled="activeSheetIndex === sheets.length - 1"
              @click="moveSheet(1)"
            >
              Move right
              <hs-icon name="arrow-right" :size="14" />
            </button>
          </div>
          <dl>
            <div><dt>Tables</dt><dd>{{ activeSheet.blocks.length }}</dd></div>
            <div><dt>Layout</dt><dd>Smart flow</dd></div>
            <div><dt>Formats</dt><dd>{{ formatLabel }}</dd></div>
          </dl>
          <p class="selection-hint">Select a table to edit its columns.</p>
        </template>
      </aside>
    </div>

    <footer class="sheet-tabs">
      <button
        v-for="(sheet, index) in sheets"
        :key="sheet.id"
        type="button"
        :class="{ 'is-active': index === activeSheetIndex }"
        @click="selectSheet(index)"
      >
        {{ sheet.name }}
      </button>
      <button
        type="button"
        class="sheet-tabs__add"
        title="Add sheet"
        :disabled="isPublished"
        @click="openNewSheetDialog"
      >
        <hs-icon name="plus" :size="16" />
      </button>
      <div class="sheet-zoom-controls" aria-label="Worksheet zoom controls">
        <button
          type="button"
          title="Zoom out"
          :disabled="sheetZoom <= 50"
          @click="changeSheetZoom(-10)"
        >
          <hs-icon name="minus" :size="15" />
        </button>
        <button
          type="button"
          class="sheet-zoom-controls__value"
          title="Reset zoom"
          @click="sheetZoom = 100"
        >
          {{ sheetZoom }}%
        </button>
        <button
          type="button"
          title="Zoom in"
          :disabled="sheetZoom >= 125"
          @click="changeSheetZoom(10)"
        >
          <hs-icon name="plus" :size="15" />
        </button>
      </div>
      <span class="sheet-tabs__status">
        {{ catalogVersion ? `Catalog ${catalogVersion}` : "Loading catalog" }}
      </span>
    </footer>

    <hs-confirm-dialog
      v-model="showPublishDialog"
      title="Publish this report?"
      message="Publishing creates an immutable version for exports, templates, and schedules. This draft will become read-only."
      confirm-text="Publish report"
      tone="success"
      :loading="isPublishing"
      @confirm="publishDefinition"
    />

    <hs-confirm-dialog
      v-model="showDraftDialog"
      title="Create a new draft version?"
      message="The published revision remains immutable. You can edit this new draft and publish it as the next version."
      confirm-text="Create draft"
      :loading="isCreatingDraft"
      @confirm="createDraftVersion"
    />

    <hs-modal
      v-model="showDataLibrary"
      :title="selectedCatalogDataset?.label || 'Add report table'"
      kicker="Business data"
      :description="selectedCatalogDataset?.description || ''"
      size="lg"
    >
      <div v-if="selectedCatalogDataset" class="data-configurator">
        <div class="data-configurator__summary">
          <span>{{ formatGrain(selectedCatalogDataset.grain) }}</span>
          <span>{{ selectedCatalogDataset.category }}</span>
          <span>{{ pendingFieldIds.length }} columns selected</span>
        </div>

        <div v-if="selectedCatalogDataset.use_cases?.length" class="use-cases">
          <span
            v-for="useCase in selectedCatalogDataset.use_cases"
            :key="useCase"
          >
            {{ useCase }}
          </span>
        </div>

        <p
          v-if="unavailableFieldCount(selectedCatalogDataset)"
          class="planned-fields-note"
        >
          Planned columns are shown for visibility but cannot be added yet.
        </p>

        <div class="field-picker">
          <label
            v-for="field in selectedCatalogDataset.fields"
            :key="field.id"
            class="field-choice"
            :class="{
              'is-unavailable': field.available === false,
              'is-manual': field.value_source === 'MANUAL',
            }"
          >
            <input
              type="checkbox"
              :checked="pendingFieldIds.includes(field.id)"
              :disabled="field.available === false"
              @change="togglePendingField(field.id)"
            />
            <span class="field-choice__main">
              <strong>{{ field.label }}</strong>
              <small>
                {{ field.value_source === 'MANUAL'
                  ? 'Blank column'
                  : field.role === 'MEASURE' ? 'Measure' : 'Column' }} ·
                {{ formatDataType(field.data_type) }}
              </small>
              <em v-if="field.value_source === 'MANUAL'">
                {{ field.description }}
              </em>
              <em v-if="field.available === false">
                {{ field.unavailable_reason }}
              </em>
            </span>
            <span class="field-choice__sample">
              {{ field.available === false
                ? 'Planned'
                : field.value_source === 'MANUAL' ? 'Empty' : fieldSample(field) }}
            </span>
          </label>
        </div>

        <div class="configured-sample">
          <p class="panel-label">Example output</p>
          <div class="configured-sample__scroll">
            <table v-if="pendingFields.length">
              <thead>
                <tr>
                  <th v-for="field in pendingFields" :key="field.id">
                    {{ field.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in 3" :key="row">
                  <td v-for="field in pendingFields" :key="field.id">
                    {{ sampleValue(field, row) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <template #footer>
        <hs-button variant="secondary" @click="showDataLibrary = false">
          Cancel
        </hs-button>
        <hs-button
          icon="plus"
          :disabled="!pendingFieldIds.length"
          @click="addConfiguredDataset"
        >
          Add to sheet
        </hs-button>
      </template>
    </hs-modal>

    <hs-modal
      v-model="showNewSheetDialog"
      title="Create worksheet"
      kicker="Workbook"
      description="Add another sheet for a separate report section."
      size="sm"
    >
      <label class="new-sheet-field">
        <span>Sheet name</span>
        <input
          v-model.trim="newSheetName"
          maxlength="31"
          placeholder="e.g. Daily Activity"
          aria-label="New sheet name"
          @keyup.enter="createSheet"
        />
        <small v-if="newSheetNameError">{{ newSheetNameError }}</small>
      </label>
      <template #footer>
        <hs-button variant="secondary" @click="showNewSheetDialog = false">
          Cancel
        </hs-button>
        <hs-button
          icon="plus"
          :disabled="!canCreateSheet"
          @click="createSheet"
        >
          Create sheet
        </hs-button>
      </template>
    </hs-modal>

    <hs-modal
      v-model="showManualColumnDialog"
      title="Add blank column"
      kicker="Table column"
      description="Create an empty column for information your team will complete after export."
      size="sm"
    >
      <div class="manual-column-form">
        <label>
          <span>Column name</span>
          <input
            v-model.trim="manualColumnName"
            maxlength="80"
            placeholder="e.g. Condition"
            aria-label="Blank column name"
            @keyup.enter="createManualColumn"
          />
        </label>
        <label>
          <span>Value type</span>
          <select v-model="manualColumnType" aria-label="Blank column type">
            <option value="STRING">Text</option>
            <option value="DECIMAL">Amount or quantity</option>
            <option value="INTEGER">Whole number</option>
            <option value="DATETIME">Date</option>
          </select>
        </label>
        <p v-if="manualColumnError" class="manual-column-error">
          {{ manualColumnError }}
        </p>
      </div>
      <template #footer>
        <hs-button variant="secondary" @click="showManualColumnDialog = false">
          Cancel
        </hs-button>
        <hs-button
          icon="plus"
          :disabled="!canCreateManualColumn"
          @click="createManualColumn"
        >
          Add column
        </hs-button>
      </template>
    </hs-modal>

    <hs-modal
      v-model="showShareDialog"
      title="Public template link"
      kicker="Share report design"
      description="Anyone with this link can inspect this data-free template and clone it into a store they can manage."
      size="sm"
    >
      <div class="template-share-panel">
        <label>
          <span>Template link</span>
          <input :value="publicTemplateUrl" readonly />
        </label>
        <p>
          The link contains the report structure and blank custom columns only.
          Store data, previews, and generated files are never shared.
        </p>
      </div>
      <template #footer>
        <hs-button variant="secondary" @click="showShareDialog = false">
          Close
        </hs-button>
        <hs-button icon="copy" @click="copyPublicTemplateUrl">
          Copy link
        </hs-button>
      </template>
    </hs-modal>
  </div>
</template>

<script>
import interact from "interactjs";
import api from "@/helper/api";
import http from "@/helper/http";
import {
  HsBadge,
  HsButton,
  HsConfirmDialog,
  HsIcon,
  HsModal,
} from "@/components/ui";
import { toast } from "vue3-toastify";
import InsightsStoreSelector from "@/features/insights/components/InsightsStoreSelector.vue";
import { applyGroupPreset } from "@/features/insights/model";
import {
  buildReportDocument,
  emptyReportQuery,
  serializeReportQuery,
  transitionDefinitionIdentity,
} from "@/features/reports/reportDocument";
import {
  createInsightsDefinition,
  executeInsightsDefinition,
  fetchInsightsCatalog,
  getInsightsDefinition,
  insightsCatalogForDesigner,
  updateInsightsDefinition,
} from "@/features/reports/reportService";
import { fetchInsightsDiscovery } from "@/features/insights/api";
import { useBackgroundTasks } from "@/composables/useBackgroundTasks";

const COLUMN_WIDTH = 132;
const ROW_HEIGHT = 30;
const MIN_SHEET_COLUMNS = 14;
const SHEET_COLUMN_PADDING = 2;

export default {
  name: "StoreReportDesignerView",
  components: {
    HsBadge,
    HsButton,
    HsConfirmDialog,
    HsIcon,
    HsModal,
    InsightsStoreSelector,
  },
  setup() {
    const backgroundTasks = useBackgroundTasks();
    return {
      addBackgroundTask: backgroundTasks.addTask,
    };
  },
  data() {
    return {
      reportName: "Untitled report",
      datasets: [],
      catalogVersion: "",
      capabilities: {},
      isLoading: false,
      loadError: "",
      isSaving: false,
      isPreviewing: false,
      isExporting: false,
      isPublishing: false,
      isCreatingDraft: false,
      isSharing: false,
      showPublishDialog: false,
      showDraftDialog: false,
      showShareDialog: false,
      publicTemplateUuid: "",
      definitionStatus: "DRAFT",
      dataSearch: "",
      showDataLibrary: false,
      selectedCatalogDatasetId: null,
      pendingFieldIds: [],
      sheetZoom: 100,
      showNewSheetDialog: false,
      newSheetName: "",
      showManualColumnDialog: false,
      manualColumnName: "",
      manualColumnType: "STRING",
      saveStatus: "",
      saveStatusTone: "neutral",
      definitionId: null,
      definitionRevision: null,
      definitionSource: null,
      collidingBlockIds: [],
      activeSheetIndex: 0,
      selectedBlockId: null,
      sheets: [{ id: "sheet-1", name: "Overview", blocks: [] }],
      reportScopeMode: "SINGLE",
      reportScopeStoreIds: [],
      reportEntityJoin: "",
      insightsStores: [],
      insightsGroups: [],
      insightsLimits: {},
    };
  },
  computed: {
    storeId() {
      return Number(this.$route.params.storeId);
    },
    activeSheet() {
      return this.sheets[this.activeSheetIndex];
    },
    selectedBlock() {
      return (
        this.activeSheet.blocks.find(
          (block) => block.id === this.selectedBlockId,
        ) || null
      );
    },
    selectedDatasetFields() {
      if (!this.selectedBlock) return [];
      return (
        this.datasets.find(
          (dataset) => dataset.id === this.selectedBlock.datasetId,
        )?.fields || []
      );
    },
    selectedDataset() {
      if (!this.selectedBlock) return null;
      return this.datasets.find(
        (dataset) => dataset.id === this.selectedBlock.datasetId,
      ) || null;
    },
    selectedSystemFields() {
      return this.selectedBlock?.fields.filter(
        (field) => field.value_source !== "MANUAL",
      ) || [];
    },
    filterableFields() {
      return this.selectedDatasetFields.filter(
        (field) => field.available !== false,
      );
    },
    selectedDateField() {
      const dateFieldId = this.selectedDataset?.default_date_field;
      return this.selectedDatasetFields.find(
        (field) => field.id === dateFieldId,
      ) || null;
    },
    selectedManualFields() {
      return this.selectedBlock?.fields.filter(
        (field) => field.value_source === "MANUAL",
      ) || [];
    },
    selectedCatalogDataset() {
      return this.datasets.find(
        (dataset) => dataset.id === this.selectedCatalogDatasetId,
      ) || null;
    },
    pendingFields() {
      if (!this.selectedCatalogDataset) return [];
      const fieldsById = new Map(
        this.selectedCatalogDataset.fields.map((field) => [field.id, field]),
      );
      return this.pendingFieldIds
        .map((fieldId) => fieldsById.get(fieldId))
        .filter(Boolean);
    },
    datasetGroups() {
      const search = this.dataSearch.toLowerCase();
      const filtered = this.datasets.filter((dataset) => {
        if (!search) return true;
        const text = [
          dataset.label,
          dataset.description,
          dataset.category,
          ...(dataset.use_cases || []),
          ...dataset.fields.map((field) => field.label),
        ].join(" ").toLowerCase();
        return text.includes(search);
      });
      const groups = new Map();
      filtered.forEach((dataset) => {
        const category = dataset.category || "Other";
        if (!groups.has(category)) groups.set(category, []);
        groups.get(category).push(dataset);
      });
      return Array.from(groups, ([category, datasets]) => ({
        category,
        datasets,
      }));
    },
    columnHeadings() {
      return Array.from({ length: this.sheetColumnCount }, (_, index) =>
        this.excelColumnLabel(index),
      );
    },
    formatLabel() {
      const formats = this.capabilities.formats || [];
      return formats.length ? formats.join(" / ") : "Unavailable";
    },
    canSave() {
      return Boolean(
        !this.isSaving &&
        !this.isPublished &&
        !this.collidingBlockIds.length &&
        this.reportName.trim() &&
        this.sheets.length &&
        this.capabilities.report_definitions,
      );
    },
    isPublished() {
      return this.definitionStatus === "PUBLISHED";
    },
    newSheetNameError() {
      const name = this.newSheetName.trim();
      if (!name) return "Enter a sheet name.";
      if (/[\\/*?:[\]]/.test(name)) {
        return "Avoid /, \\, *, ?, :, [ and ].";
      }
      if (this.sheets.some(
        (sheet) => sheet.name.trim().toLowerCase() === name.toLowerCase(),
      )) {
        return "A sheet with this name already exists.";
      }
      return "";
    },
    canCreateSheet() {
      return Boolean(
        !this.isPublished &&
        this.sheets.length < 10 &&
        !this.newSheetNameError,
      );
    },
    manualColumnError() {
      const name = this.manualColumnName.trim();
      if (!name) return "Enter a column name.";
      if (this.selectedBlock?.fields.some(
        (field) => field.label.trim().toLowerCase() === name.toLowerCase(),
      )) {
        return "This table already has a column with that name.";
      }
      return "";
    },
    canCreateManualColumn() {
      return Boolean(
        this.selectedBlock &&
        !this.isPublished &&
        this.selectedBlock.fields.length < 50 &&
        !this.manualColumnError,
      );
    },
    sheetZoomStyle() {
      return { zoom: this.sheetZoom / 100 };
    },
    sheetRowCount() {
      return (this.activeSheet?.blocks || []).reduce((rowCount, block) => {
        const blockEndRow = block.row + this.blockRowSpan(block) + 2;
        return Math.max(rowCount, blockEndRow);
      }, 34);
    },
    sheetColumnCount() {
      return (this.activeSheet?.blocks || []).reduce((columnCount, block) => {
        const dragOffset = Math.max(
          0,
          Math.round((block.dragX || 0) / COLUMN_WIDTH),
        );
        const blockEndColumn =
          block.column + dragOffset + Math.max(block.fields.length, 2);
        return Math.max(
          columnCount,
          blockEndColumn + SHEET_COLUMN_PADDING,
        );
      }, MIN_SHEET_COLUMNS);
    },
    sheetViewportStyle() {
      const sheetWidth = this.sheetColumnCount * COLUMN_WIDTH;
      return {
        gridTemplateColumns: `40px ${sheetWidth}px`,
        gridTemplateRows: `${ROW_HEIGHT}px ${this.sheetRowCount * ROW_HEIGHT}px`,
        minWidth: `${sheetWidth + 40}px`,
      };
    },
    columnHeadingsStyle() {
      return {
        gridTemplateColumns: `repeat(${this.sheetColumnCount}, ${COLUMN_WIDTH}px)`,
      };
    },
    rowHeadingsStyle() {
      return {
        gridTemplateRows: `repeat(${this.sheetRowCount}, ${ROW_HEIGHT}px)`,
      };
    },
    virtualSheetStyle() {
      return {
        minHeight: `${this.sheetRowCount * ROW_HEIGHT}px`,
        width: `${this.sheetColumnCount * COLUMN_WIDTH}px`,
      };
    },
    publicTemplateUrl() {
      if (!this.publicTemplateUuid) return "";
      return `${window.location.origin}/report-templates/${this.publicTemplateUuid}`;
    },
  },
  async created() {
    const insightsDefinitionId = Number(
      this.$route.query.insightsDefinitionId,
    );
    if (Number.isInteger(insightsDefinitionId) && insightsDefinitionId > 0) {
      this.reportScopeMode = "MULTI";
      await this.fetchInsightsDesignerContext();
      await this.loadInsightsDefinition(insightsDefinitionId);
    } else {
      this.reportScopeStoreIds = [String(this.storeId)];
      await this.fetchDatasets();
      const reportId = Number(this.$route.query.reportId);
      if (Number.isInteger(reportId) && reportId > 0) {
        await this.loadDefinition(reportId);
      }
    }
  },
  mounted() {
    this.setupBlockDragging();
  },
  beforeUnmount() {
    interact(".report-table-block").unset();
  },
  methods: {
    buildDocument() {
      return buildReportDocument({
        catalogVersion: this.catalogVersion,
        sheets: this.sheets,
      });
    },
    async saveDefinition() {
      if (!this.canSave) return false;
      this.isSaving = true;
      this.saveStatus = "Saving";
      this.saveStatusTone = "neutral";
      const payload = {
        name: this.reportName,
        document: this.buildDocument(),
      };
      try {
        let response;
        if (this.reportScopeMode === "MULTI") {
          const insightsPayload = {
            ...payload,
            description: "",
            scope: {
              store_ids: this.reportScopeStoreIds.map(Number),
            },
            entity_resolution: this.reportEntityJoin
              ? { product: { join_by: this.reportEntityJoin } }
              : {},
            display_config: { editor: "ADVANCED" },
          };
          const definition = this.definitionId
            && this.definitionSource === "INSIGHTS"
            ? await updateInsightsDefinition(this.definitionId, {
                ...insightsPayload,
                expected_revision: this.definitionRevision,
              })
            : await createInsightsDefinition(insightsPayload);
          response = { data: { data: { definition } } };
        } else if (this.definitionId && this.definitionSource === "STORE") {
          response = await http.patch(
            api.store_report_definition(this.storeId, this.definitionId),
            { ...payload, expected_revision: this.definitionRevision },
          );
        } else {
          response = await http.post(
            api.store_report_definitions(this.storeId),
            payload,
          );
        }
        const definition = response?.data?.data?.definition;
        this.definitionId = definition.id;
        this.definitionRevision = definition.revision;
        this.definitionSource =
          this.reportScopeMode === "MULTI" ? "INSIGHTS" : "STORE";
        this.definitionStatus = definition.status;
        this.saveStatus = `Saved · v${definition.revision}`;
        this.saveStatusTone = "success";
        await this.$router.replace({
          query: this.reportScopeMode === "MULTI"
            ? {
                insightsDefinitionId: definition.id,
                revision: definition.revision,
              }
            : { ...this.$route.query, reportId: definition.id },
        });
        return true;
      } catch (error) {
        const errorCode = error?.response?.data?.error_code;
        this.saveStatus = errorCode === "report_revision_conflict"
          ? "Newer version available"
          : "Save failed";
        this.saveStatusTone = "danger";
        toast.error(
          errorCode === "report_revision_conflict"
            ? "This report changed elsewhere. Reload before saving again."
            : "The report draft could not be saved.",
        );
        return false;
      } finally {
        this.isSaving = false;
      }
    },
    emptyBlockQuery() {
      return emptyReportQuery();
    },
    hydrateBlockQuery(savedQuery = null, dataset = null) {
      const query = {
        ...this.emptyBlockQuery(),
        scheduled_period_mode:
          dataset?.scheduled_period_modes?.[0]?.id || "ALL",
        ...(savedQuery ? JSON.parse(JSON.stringify(savedQuery)) : {}),
      };
      query.filters = (query.filters || []).map((filter) => ({
        ...filter,
        value: Array.isArray(filter.value)
          ? filter.value.join(", ")
          : filter.value ?? "",
        value_to: Array.isArray(filter.value) ? filter.value[1] : "",
      }));
      query.aggregations = query.aggregations || [];
      query.sort = query.sort || [];
      return query;
    },
    serializeBlockQuery(block) {
      return serializeReportQuery(block.query || this.emptyBlockQuery());
    },
    async previewDefinition() {
      if (this.isPreviewing) return;
      const saved = this.isPublished || await this.saveDefinition();
      if (!saved || !this.definitionId) return;
      if (this.reportScopeMode === "MULTI") {
        await this.queueInsightsDefinition("JSON");
        return;
      }
      this.isPreviewing = true;
      try {
        const response = await http.get(
          api.store_report_definition_preview(
            this.storeId,
            this.definitionId,
          ),
        );
        const previewSheets = response?.data?.data?.preview?.sheets || [];
        previewSheets.forEach((previewSheet) => {
          const sheet = this.sheets.find(
            (item) => item.id === previewSheet.id,
          );
          if (!sheet) return;
          previewSheet.blocks.forEach((previewBlock) => {
            const block = sheet.blocks.find(
              (item) => item.id === previewBlock.id,
            );
            if (!block) return;
            block.previewRows = previewBlock.rows || [];
            block.previewHasMore = Boolean(previewBlock.has_more);
            block.row = previewBlock.position.row;
            block.column = previewBlock.position.column;
          });
        });
        this.scheduleLayoutResolution();
        this.saveStatus = "Live data preview";
        this.saveStatusTone = "success";
      } catch (error) {
        toast.error(
          error?.response?.data?.error?.message ||
          "The live report preview could not be loaded.",
        );
      } finally {
        this.isPreviewing = false;
      }
    },
    async downloadReport(format) {
      if (this.isExporting) return;
      const saved = this.isPublished || await this.saveDefinition();
      if (!saved || !this.definitionId) return;
      if (this.reportScopeMode === "MULTI") {
        await this.queueInsightsDefinition(format);
        return;
      }
      this.isExporting = true;
      try {
        const response = await http.get(
          api.store_report_definition_export(
            this.storeId,
            this.definitionId,
            format,
          ),
          { responseType: "blob" },
        );
        const disposition = response.headers?.["content-disposition"] || "";
        const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
        const plainName = disposition.match(/filename="?([^";]+)"?/i)?.[1];
        const filename = encodedName
          ? decodeURIComponent(encodedName)
          : plainName || `${this.reportName}.${format.toLowerCase()}`;
        const downloadUrl = URL.createObjectURL(response.data);
        const anchor = document.createElement("a");
        anchor.href = downloadUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(downloadUrl);
        this.saveStatus = `${format} downloaded`;
        this.saveStatusTone = "success";
      } catch (error) {
        toast.error(`The ${format} report could not be generated.`);
      } finally {
        this.isExporting = false;
      }
    },
    async publishDefinition() {
      if (this.isPublishing || !this.definitionId) return;
      const saved = await this.saveDefinition();
      if (!saved) return;
      this.isPublishing = true;
      try {
        const response = await http.post(
          api.store_report_definition_publish(
            this.storeId,
            this.definitionId,
          ),
          { expected_revision: this.definitionRevision },
        );
        const definition = response?.data?.data?.definition;
        this.definitionStatus = definition.status;
        this.definitionRevision = definition.revision;
        this.showPublishDialog = false;
        this.saveStatus = `Published · v${definition.revision}`;
        this.saveStatusTone = "success";
        toast.success("Report published");
      } catch (error) {
        toast.error("The report could not be published.");
      } finally {
        this.isPublishing = false;
      }
    },
    async sharePublishedTemplate() {
      if (!this.isPublished || !this.definitionId || this.isSharing) return;
      this.isSharing = true;
      try {
        const response = await http.post(
          api.store_report_definition_public_template(
            this.storeId,
            this.definitionId,
          ),
        );
        this.publicTemplateUuid = response?.data?.data?.template?.uuid || "";
        this.showShareDialog = Boolean(this.publicTemplateUuid);
      } catch (error) {
        toast.error("The public template link could not be created.");
      } finally {
        this.isSharing = false;
      }
    },
    async createDraftVersion() {
      if (!this.isPublished || !this.definitionId || this.isCreatingDraft) {
        return;
      }
      this.isCreatingDraft = true;
      try {
        const response = await http.post(
          api.store_report_definition_draft(this.storeId, this.definitionId),
        );
        const definition = response?.data?.data?.definition;
        this.definitionStatus = definition.status;
        this.definitionRevision = definition.revision;
        this.showDraftDialog = false;
        this.saveStatus = `Draft · v${definition.revision}`;
        this.saveStatusTone = "warning";
        toast.success("New draft version created");
      } catch (error) {
        toast.error("A new draft version could not be created.");
      } finally {
        this.isCreatingDraft = false;
      }
    },
    async copyPublicTemplateUrl() {
      if (!this.publicTemplateUrl) return;
      try {
        await navigator.clipboard.writeText(this.publicTemplateUrl);
        toast.success("Template link copied");
      } catch (error) {
        toast.error("Copy the template link from the field.");
      }
    },
    async loadDefinition(definitionId) {
      this.isLoading = true;
      try {
        const response = await http.get(
          api.store_report_definition(this.storeId, definitionId),
        );
        const definition = response?.data?.data?.definition;
        this.definitionSource = "STORE";
        this.hydrateDefinitionDocument(definition);
      } catch (error) {
        this.loadError =
          error?.response?.data?.error?.message ||
          "The report draft could not be loaded.";
      } finally {
        this.isLoading = false;
      }
    },
    hydrateDefinitionDocument(definition) {
      this.reportName = definition.name;
      this.definitionId = definition.id;
      this.definitionRevision = definition.revision;
      this.definitionStatus = definition.status || "DRAFT";
      this.sheets = definition.document.sheets.map((sheet) => ({
          id: sheet.id,
          name: sheet.name,
          blocks: sheet.blocks.map((block) => {
            const dataset = this.datasets.find(
              (item) => item.id === block.dataset_id,
            );
            const fieldsById = new Map(
              (dataset?.fields || []).map((field) => [field.id, field]),
            );
            const systemFields = (block.field_ids || [])
              .map((fieldId) => fieldsById.get(fieldId))
              .filter(Boolean);
            const manualFields = (block.manual_columns || []).map((field) => ({
              ...field,
              value_source: "MANUAL",
              available: true,
              sample_values: [null, null, null],
            }));
            const allFields = [...systemFields, ...manualFields];
            const allFieldsById = new Map(
              allFields.map((field) => [field.id, field]),
            );
            const orderedFields = (block.column_order || allFields.map(
              (field) => field.id,
            )).map((fieldId) => allFieldsById.get(fieldId)).filter(Boolean);
            return {
              id: block.id,
              datasetId: block.dataset_id,
              name: block.name,
              grain: dataset?.grain || "TABLE",
              fields: orderedFields,
              row: block.position.row,
              column: block.position.column,
              layoutMode: block.layout_mode || "FLOW",
              dragX: 0,
              dragY: 0,
              previewRows: null,
              previewHasMore: false,
              query: this.hydrateBlockQuery(block.query, dataset),
            };
          }),
      }));
      this.activeSheetIndex = 0;
      this.selectedBlockId = null;
      this.scheduleLayoutResolution();
      this.saveStatus = `Saved · v${definition.revision}`;
      this.saveStatusTone = "success";
    },
    async fetchInsightsDesignerContext() {
      this.isLoading = true;
      this.loadError = "";
      try {
        const [discovery, catalog] = await Promise.all([
          fetchInsightsDiscovery(),
          fetchInsightsCatalog(),
        ]);
        const designerCatalog = insightsCatalogForDesigner(catalog);
        this.insightsStores = discovery.stores;
        this.insightsGroups = discovery.groups;
        this.insightsLimits = discovery.limits;
        this.datasets = designerCatalog.datasets;
        this.catalogVersion = designerCatalog.catalog_version;
        this.capabilities = designerCatalog.capabilities;
      } catch (error) {
        this.loadError =
          error?.response?.data?.error?.message ||
          error?.message ||
          "Insights report data could not be loaded.";
      } finally {
        this.isLoading = false;
      }
    },
    async loadInsightsDefinition(definitionId) {
      this.isLoading = true;
      try {
        const definition = await getInsightsDefinition(definitionId);
        this.definitionSource = "INSIGHTS";
        this.reportScopeStoreIds = (definition.scope?.store_ids || []).map(String);
        this.reportEntityJoin =
          definition.entity_resolution?.product?.join_by || "";
        this.hydrateDefinitionDocument(definition);
      } catch (error) {
        this.loadError =
          error?.response?.data?.error?.message ||
          "The Insights definition could not be loaded.";
      } finally {
        this.isLoading = false;
      }
    },
    async handleScopeModeChange() {
      const targetSource =
        this.reportScopeMode === "MULTI" ? "INSIGHTS" : "STORE";
      const identity = transitionDefinitionIdentity(
        {
          id: this.definitionId,
          revision: this.definitionRevision,
          source: this.definitionSource,
        },
        targetSource,
      );
      this.definitionId = identity.id;
      this.definitionRevision = identity.revision;
      this.definitionSource = identity.source;
      if (this.reportScopeMode === "MULTI") {
        await this.fetchInsightsDesignerContext();
        if (!this.reportScopeStoreIds.length) {
          this.reportScopeStoreIds = [String(this.storeId)];
        }
        return;
      }
      this.reportScopeStoreIds = [String(this.storeId)];
      this.reportEntityJoin = "";
      await this.fetchDatasets();
    },
    applyInsightsGroup(group) {
      this.reportScopeStoreIds = applyGroupPreset(
        group,
        this.insightsStores.map((store) => store.id),
        this.insightsLimits.maxStores,
      );
    },
    async queueInsightsDefinition(format) {
      if (format === "JSON") this.isPreviewing = true;
      else this.isExporting = true;
      try {
        const task = await executeInsightsDefinition(this.definitionId, format);
        this.addBackgroundTask(
          task.task_id,
          "insights_definition",
          {
            label: `${this.reportName} ${format === "JSON" ? "preview" : "export"}`,
            taskRecordId: task.task_record_id,
            statusUrl: task.status_url,
          },
          null,
        );
        this.saveStatus = "Queued in background";
        this.saveStatusTone = "success";
        toast.success("Insights report queued. Track it from background tasks.");
      } catch (error) {
        toast.error(
          error?.response?.data?.error?.message ||
          "The Insights report could not be queued.",
        );
      } finally {
        this.isPreviewing = false;
        this.isExporting = false;
      }
    },
    setupBlockDragging() {
      interact(".report-table-block").draggable({
        allowFrom: ".report-table-block__title",
        listeners: {
          start: (event) => {
            this.selectBlock(event.target.dataset.blockId);
            const block = this.findActiveBlock(event.target.dataset.blockId);
            if (block) {
              block.dragOrigin = {
                row: block.row,
                column: block.column,
              };
            }
          },
          move: (event) => {
            if (this.isPublished) return;
            const block = this.findActiveBlock(event.target.dataset.blockId);
            if (!block) return;
            block.dragX += event.dx;
            block.dragY += event.dy;
          },
          end: (event) => {
            if (this.isPublished) return;
            const block = this.findActiveBlock(event.target.dataset.blockId);
            if (!block) return;
            block.column = Math.max(
              0,
              Math.round(
                (block.column * COLUMN_WIDTH + block.dragX) / COLUMN_WIDTH,
              ),
            );
            block.row = Math.max(
              0,
              Math.round((block.row * ROW_HEIGHT + block.dragY) / ROW_HEIGHT),
            );
            block.dragX = 0;
            block.dragY = 0;
            this.$nextTick(() => {
              if (
                block.layoutMode === "FIXED" &&
                this.blockCollides(block, true)
              ) {
                block.row = block.dragOrigin?.row ?? block.row;
                block.column = block.dragOrigin?.column ?? block.column;
                toast.error(
                  "Exact-position tables cannot overlap. The table was moved back.",
                );
              }
              block.dragOrigin = null;
              this.resolveActiveSheetLayout();
            });
          },
        },
      });
    },
    openManualColumnDialog() {
      if (!this.selectedBlock || this.isPublished) return;
      this.manualColumnName = "";
      this.manualColumnType = "STRING";
      this.showManualColumnDialog = true;
    },
    createManualColumn() {
      if (!this.canCreateManualColumn) return;
      this.selectedBlock.fields.push({
        id: `manual.${Date.now().toString(36)}`,
        label: this.manualColumnName.trim(),
        data_type: this.manualColumnType,
        role: "DIMENSION",
        available: true,
        value_source: "MANUAL",
        sample_values: [null, null, null],
      });
      this.showManualColumnDialog = false;
      this.manualColumnName = "";
      this.scheduleLayoutResolution();
    },
    removeManualField(fieldId) {
      if (!this.selectedBlock || this.isPublished) return;
      const index = this.selectedFieldIndex(fieldId);
      if (index >= 0) {
        this.selectedBlock.fields.splice(index, 1);
        this.scheduleLayoutResolution();
      }
    },
    async fetchDatasets() {
      this.isLoading = true;
      this.loadError = "";
      try {
        const response = await http.get(
          api.store_report_datasets(this.storeId),
        );
        const catalog = response?.data?.data || {};
        this.datasets = catalog.datasets || [];
        this.catalogVersion = catalog.catalog_version || "";
        this.capabilities = catalog.capabilities || {};
      } catch (error) {
        this.datasets = [];
        this.loadError =
          error?.response?.data?.message ||
          "The report dataset catalog could not be loaded.";
      } finally {
        this.isLoading = false;
      }
    },
    goBack() {
      this.$router.push({
        name: "reports",
        params: { storeId: this.storeId },
      });
    },
    openDatasetPicker(dataset) {
      if (this.isPublished) return;
      this.selectedCatalogDatasetId = dataset.id;
      this.pendingFieldIds = dataset.default_columns.filter((fieldId) =>
        dataset.fields.some(
          (field) => field.id === fieldId && field.available !== false,
        ),
      );
      this.showDataLibrary = true;
    },
    togglePendingField(fieldId) {
      const index = this.pendingFieldIds.indexOf(fieldId);
      if (index >= 0) {
        if (this.pendingFieldIds.length === 1) return;
        this.pendingFieldIds.splice(index, 1);
        return;
      }
      this.pendingFieldIds.push(fieldId);
    },
    addConfiguredDataset() {
      if (!this.selectedCatalogDataset || !this.pendingFieldIds.length) return;
      this.addDataset(this.selectedCatalogDataset, this.pendingFieldIds);
      this.showDataLibrary = false;
    },
    addDataset(dataset, selectedFieldIds = dataset.default_columns) {
      if (this.isPublished) return;
      const fieldsById = new Map(
        dataset.fields.map((field) => [field.id, field]),
      );
      const fields = selectedFieldIds
        .map((fieldId) => fieldsById.get(fieldId))
        .filter(Boolean);
      const blockIndex = this.activeSheet.blocks.length;
      this.activeSheet.blocks.push({
        id: `${dataset.id}-${Date.now()}-${blockIndex}`,
        datasetId: dataset.id,
        name: dataset.label,
        grain: dataset.grain,
        fields,
        row: 2 + Math.floor(blockIndex / 2) * 8,
        column: blockIndex % 2 === 0 ? 1 : 7,
        layoutMode: "FLOW",
        dragX: 0,
        dragY: 0,
        previewRows: null,
        previewHasMore: false,
        query: {
          ...this.emptyBlockQuery(),
          scheduled_period_mode:
            dataset.scheduled_period_modes?.[0]?.id || "ALL",
        },
      });
      this.selectedBlockId = this.activeSheet.blocks.at(-1).id;
      this.scheduleLayoutResolution();
    },
    handleLayoutModeChange() {
      if (!this.selectedBlock) return;
      this.$nextTick(() => {
        if (
          this.selectedBlock.layoutMode === "FIXED" &&
          this.blockCollides(this.selectedBlock, true)
        ) {
          this.selectedBlock.layoutMode = "FLOW";
          toast.error(
            "Move the table to an open area before using exact position.",
          );
        }
        this.resolveActiveSheetLayout();
      });
    },
    scheduleLayoutResolution() {
      this.$nextTick(() => this.resolveActiveSheetLayout());
    },
    blockRowSpan(block) {
      const element = Array.from(
        document.querySelectorAll(".report-table-block"),
      ).find((candidate) => candidate.dataset.blockId === block.id);
      if (element?.offsetHeight) {
        return Math.max(1, Math.ceil(element.offsetHeight / ROW_HEIGHT));
      }
      const guideRows = this.blockFieldGuide(block).length;
      return this.displayedRows(block).length + 3 + guideRows;
    },
    blockBounds(block, row = block.row) {
      return {
        rowStart: row,
        rowEnd: row + this.blockRowSpan(block) - 1,
        columnStart: block.column,
        columnEnd: block.column + Math.max(block.fields.length, 2) - 1,
      };
    },
    boundsOverlap(left, right) {
      return !(
        left.columnEnd < right.columnStart ||
        right.columnEnd < left.columnStart ||
        left.rowEnd < right.rowStart ||
        right.rowEnd < left.rowStart
      );
    },
    blockCollides(block, fixedOnly = false) {
      const bounds = this.blockBounds(block);
      return this.activeSheet.blocks.some(
        (candidate) =>
          candidate.id !== block.id &&
          (!fixedOnly || candidate.layoutMode === "FIXED") &&
          this.boundsOverlap(bounds, this.blockBounds(candidate)),
      );
    },
    resolveActiveSheetLayout() {
      if (!this.activeSheet) return;
      const fixedBlocks = this.activeSheet.blocks
        .filter((block) => block.layoutMode === "FIXED")
        .sort((left, right) => left.row - right.row || left.column - right.column);
      const flowBlocks = this.activeSheet.blocks
        .filter((block) => block.layoutMode !== "FIXED")
        .sort((left, right) => left.row - right.row || left.column - right.column);
      const placed = [];
      const collisions = new Set();

      fixedBlocks.forEach((block) => {
        const bounds = this.blockBounds(block);
        placed.forEach(({ block: placedBlock, bounds: placedBounds }) => {
          if (this.boundsOverlap(bounds, placedBounds)) {
            collisions.add(block.id);
            collisions.add(placedBlock.id);
          }
        });
        placed.push({ block, bounds });
      });

      flowBlocks.forEach((block) => {
        let resolvedRow = block.row;
        let bounds = this.blockBounds(block, resolvedRow);
        let overlapping = placed.filter((entry) =>
          this.boundsOverlap(bounds, entry.bounds),
        );
        while (overlapping.length) {
          resolvedRow = Math.max(
            ...overlapping.map((entry) => entry.bounds.rowEnd),
          ) + 2;
          bounds = this.blockBounds(block, resolvedRow);
          overlapping = placed.filter((entry) =>
            this.boundsOverlap(bounds, entry.bounds),
          );
        }
        block.row = resolvedRow;
        placed.push({ block, bounds });
      });
      this.collidingBlockIds = Array.from(collisions);
    },
    toggleDateRange(event) {
      if (!this.selectedBlock || !this.selectedDateField) return;
      if (!event.target.checked) {
        this.selectedBlock.query.date_range = null;
        return;
      }
      const now = new Date();
      const localToday = new Date(
        now.getTime() - now.getTimezoneOffset() * 60000,
      ).toISOString().slice(0, 10);
      this.selectedBlock.query.date_range = {
        field_id: this.selectedDateField.id,
        start_date: localToday,
        end_date: localToday,
      };
    },
    addQueryFilter() {
      const field = this.filterableFields[0];
      if (!this.selectedBlock || !field) return;
      this.selectedBlock.query.filters.push({
        field_id: field.id,
        operator: field.filter_operators?.[0] || "EQUALS",
        value: "",
        value_to: "",
      });
    },
    resetFilterOperator(filter) {
      filter.operator = this.filterOperators(filter.field_id)[0] || "EQUALS";
      filter.value = "";
      filter.value_to = "";
    },
    filterOperators(fieldId) {
      return this.selectedDatasetFields.find(
        (field) => field.id === fieldId,
      )?.filter_operators || [];
    },
    filterInputType(fieldId) {
      const dataType = this.selectedDatasetFields.find(
        (field) => field.id === fieldId,
      )?.data_type;
      if (["INTEGER", "DECIMAL"].includes(dataType)) return "number";
      if (dataType === "DATETIME") return "date";
      return "text";
    },
    isValuelessOperator(operator) {
      return ["IS_EMPTY", "IS_NOT_EMPTY"].includes(operator);
    },
    formatOperator(operator) {
      return String(operator || "")
        .toLowerCase()
        .replaceAll("_", " ")
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    fieldLabel(fieldId) {
      return this.selectedDatasetFields.find(
        (field) => field.id === fieldId,
      )?.label || fieldId;
    },
    aggregationOptions(fieldId) {
      return this.selectedDatasetFields.find(
        (field) => field.id === fieldId,
      )?.aggregations || ["COUNT"];
    },
    setGroupingPeriod(period) {
      if (!this.selectedBlock || !this.selectedDateField) return;
      if (period === "NONE") {
        this.selectedBlock.query.grouping = null;
        this.selectedBlock.query.aggregations = [];
        this.scheduleLayoutResolution();
        return;
      }
      if (!this.isFieldSelected(this.selectedDateField.id)) {
        this.selectedBlock.fields.push(this.selectedDateField);
      }
      this.selectedBlock.query.grouping = {
        period,
        date_field_id: this.selectedDateField.id,
        field_ids: [],
      };
      this.syncGroupingRules();
      this.scheduleLayoutResolution();
    },
    syncGroupingRules() {
      const grouping = this.selectedBlock?.query?.grouping;
      if (!grouping) return;
      const selectedFields = this.selectedSystemFields.filter(
        (field) => field.id !== grouping.date_field_id,
      );
      grouping.field_ids = selectedFields
        .filter((field) => field.groupable)
        .map((field) => field.id);
      const existingFunctions = new Map(
        this.selectedBlock.query.aggregations.map(
          (aggregation) => [aggregation.field_id, aggregation.function],
        ),
      );
      this.selectedBlock.query.aggregations = selectedFields
        .filter((field) => !field.groupable)
        .map((field) => ({
          field_id: field.id,
          function: existingFunctions.get(field.id) ||
            field.aggregations?.[0] || "COUNT",
        }));
    },
    addQuerySort() {
      const field = this.selectedSystemFields[0];
      if (!this.selectedBlock || !field) return;
      this.selectedBlock.query.sort.push({
        field_id: field.id,
        direction: "ASC",
      });
    },
    openNewSheetDialog() {
      if (this.isPublished || this.sheets.length >= 10) return;
      let number = this.sheets.length + 1;
      let suggestedName = `Sheet ${number}`;
      while (this.sheets.some((sheet) => sheet.name === suggestedName)) {
        number += 1;
        suggestedName = `Sheet ${number}`;
      }
      this.newSheetName = suggestedName;
      this.showNewSheetDialog = true;
    },
    createSheet() {
      if (!this.canCreateSheet) return;
      this.sheets.push({
        id: `sheet-${Date.now()}`,
        name: this.newSheetName.trim(),
        blocks: [],
      });
      this.activeSheetIndex = this.sheets.length - 1;
      this.selectedBlockId = null;
      this.showNewSheetDialog = false;
      this.newSheetName = "";
    },
    changeSheetZoom(delta) {
      this.sheetZoom = Math.min(125, Math.max(50, this.sheetZoom + delta));
    },
    selectSheet(index) {
      this.activeSheetIndex = index;
      this.selectedBlockId = null;
    },
    moveSheet(offset) {
      if (this.isPublished) return;
      const nextIndex = this.activeSheetIndex + offset;
      if (nextIndex < 0 || nextIndex >= this.sheets.length) return;
      const [sheet] = this.sheets.splice(this.activeSheetIndex, 1);
      this.sheets.splice(nextIndex, 0, sheet);
      this.activeSheetIndex = nextIndex;
    },
    selectBlock(blockId) {
      this.selectedBlockId = blockId;
    },
    clearSelection() {
      this.selectedBlockId = null;
    },
    findActiveBlock(blockId) {
      return this.activeSheet.blocks.find((block) => block.id === blockId);
    },
    removeSelectedBlock() {
      if (this.isPublished) return;
      const blockIndex = this.activeSheet.blocks.findIndex(
        (block) => block.id === this.selectedBlockId,
      );
      if (blockIndex < 0) return;
      this.activeSheet.blocks.splice(blockIndex, 1);
      this.selectedBlockId = null;
    },
    isFieldSelected(fieldId) {
      return this.selectedFieldIndex(fieldId) >= 0;
    },
    isOnlySelectedField(fieldId) {
      const systemFieldCount = this.selectedBlock?.fields.filter(
        (field) => field.value_source !== "MANUAL",
      ).length || 0;
      return systemFieldCount === 1 && this.isFieldSelected(fieldId);
    },
    selectedFieldIndex(fieldId) {
      return (
        this.selectedBlock?.fields.findIndex((field) => field.id === fieldId) ??
        -1
      );
    },
    toggleField(field) {
      if (!this.selectedBlock || this.isPublished) return;
      const fieldIndex = this.selectedFieldIndex(field.id);
      if (fieldIndex >= 0) {
        if (this.selectedBlock.fields.length === 1) return;
        this.selectedBlock.fields.splice(fieldIndex, 1);
        this.selectedBlock.query.sort = this.selectedBlock.query.sort.filter(
          (sort) => sort.field_id !== field.id,
        );
        if (field.id === this.selectedBlock.query.grouping?.date_field_id) {
          this.selectedBlock.query.grouping = null;
          this.selectedBlock.query.aggregations = [];
        } else {
          this.syncGroupingRules();
        }
        this.scheduleLayoutResolution();
        return;
      }
      this.selectedBlock.fields.push(field);
      this.syncGroupingRules();
      this.scheduleLayoutResolution();
    },
    moveField(fieldId, offset) {
      if (!this.selectedBlock || this.isPublished) return;
      const currentIndex = this.selectedFieldIndex(fieldId);
      const nextIndex = currentIndex + offset;
      if (
        currentIndex < 0 ||
        nextIndex < 0 ||
        nextIndex >= this.selectedBlock.fields.length
      ) {
        return;
      }
      const [field] = this.selectedBlock.fields.splice(currentIndex, 1);
      this.selectedBlock.fields.splice(nextIndex, 0, field);
    },
    cellReference(column, row) {
      return `${this.excelColumnLabel(column)}${row + 1}`;
    },
    excelColumnLabel(column) {
      let remaining = column + 1;
      let label = "";
      while (remaining > 0) {
        const characterIndex = (remaining - 1) % 26;
        label = String.fromCharCode(65 + characterIndex) + label;
        remaining = Math.floor((remaining - 1) / 26);
      }
      return label;
    },
    blockStyle(block) {
      return {
        left: `${block.column * COLUMN_WIDTH + block.dragX}px`,
        top: `${block.row * ROW_HEIGHT + block.dragY}px`,
        width: `${Math.max(block.fields.length, 2) * COLUMN_WIDTH}px`,
      };
    },
    datasetIcon(datasetId) {
      return {
        customers: "users",
        products: "package",
        sale_carts: "shopping-cart",
        product_sale_lines: "bar-chart-2",
        inventory_current_stock: "layers",
        purchases: "truck",
        expenses: "credit-card",
        services: "tool",
        service_sale_lines: "bar-chart-2",
        daily_sales_products: "bar-chart-2",
        daily_sales_services: "tool",
        daily_transaction_summary: "pie-chart",
        daily_transaction_payments: "credit-card",
        daily_transaction_carts: "shopping-cart",
        daily_transaction_lines: "list",
        daily_transaction_installments: "calendar",
        daily_transaction_installment_payments: "dollar-sign",
      }[datasetId] || "database";
    },
    systemFieldCount(dataset) {
      return dataset.fields.filter(
        (field) => field.available !== false && field.value_source !== "MANUAL",
      ).length;
    },
    manualFieldCount(dataset) {
      return dataset.fields.filter(
        (field) => field.value_source === "MANUAL",
      ).length;
    },
    unavailableFieldCount(dataset) {
      return dataset.fields.filter((field) => field.available === false).length;
    },
    formatDataType(dataType) {
      return {
        STRING: "Text",
        INTEGER: "Number",
        DECIMAL: "Amount",
        DATETIME: "Date",
        ENUM: "Status",
      }[dataType] || dataType;
    },
    fieldSample(field) {
      return this.sampleValue(field, 1);
    },
    formatGrain(grain) {
      return String(grain || "")
        .replaceAll("_", " ")
        .toLowerCase();
    },
    sampleValue(field, row) {
      const value = field.sample_values?.[row - 1];
      if (value === null || value === undefined || value === "") return "—";
      if (field.data_type === "DATETIME") {
        return new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "UTC",
        }).format(new Date(value));
      }
      if (field.data_type === "DECIMAL") {
        return new Intl.NumberFormat("en-RW", {
          maximumFractionDigits: 2,
        }).format(value);
      }
      return String(value);
    },
    displayedRows(block) {
      if (Array.isArray(block.previewRows)) return block.previewRows;
      return [1, 2, 3];
    },
    displayCellValue(block, field, row, rowIndex) {
      const value = Array.isArray(block.previewRows)
        ? row[field.id]
        : field.sample_values?.[rowIndex];
      if (field.value_source === "MANUAL") return "";
      if (value === null || value === undefined || value === "") return "—";
      if (field.data_type === "DATETIME") {
        return new Intl.DateTimeFormat("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          timeZone: "UTC",
        }).format(new Date(value));
      }
      if (field.data_type === "DECIMAL") {
        return new Intl.NumberFormat("en-RW", {
          maximumFractionDigits: 2,
        }).format(value);
      }
      return String(value);
    },
    blockFieldGuide(block) {
      const grouping = block.query?.grouping;
      const aggregations = new Map(
        (block.query?.aggregations || []).map(
          (aggregation) => [aggregation.field_id, aggregation.function],
        ),
      );
      return block.fields.flatMap((field) => {
        const aggregation = aggregations.get(field.id);
        if (aggregation) {
          return [{
            id: field.id,
            label: field.label,
            meaning: field.description || null,
            calculation: field.calculation || null,
            rollup: `${this.formatOperator(aggregation)} for each ${grouping.period.toLowerCase()} bucket and selected grouping dimensions.`,
          }];
        }
        if (field.id === grouping?.date_field_id) {
          return [{
            id: field.id,
            label: field.label,
            meaning: "Store-local reporting period containing the source timestamp.",
            calculation: `Source time converted to store local time and bucketed by ${grouping.period.toLowerCase()}.`,
            rollup: null,
          }];
        }
        if (field.value_kind === "DERIVED" || field.calculation) {
          return [{
            id: field.id,
            label: field.label,
            meaning: field.description || null,
            calculation: field.calculation || null,
            rollup: null,
          }];
        }
        return [];
      });
    },
  },
};
</script>

<style scoped>
.designer-scope {
  align-items: start;
  background: #f8fafa;
  border: 1px solid var(--line, #d7e0e2);
  border-radius: 14px;
  display: grid;
  gap: 0.75rem;
  grid-template-columns: minmax(180px, 1fr) minmax(160px, 220px) minmax(180px, 240px);
  margin: 0.75rem;
  padding: 0.9rem;
}

.designer-scope h2 {
  font-size: 1rem;
  margin: 0.2rem 0 0;
}

.designer-scope > label {
  display: grid;
  gap: 0.35rem;
}

.designer-scope > label span {
  font-size: 0.75rem;
  font-weight: 800;
}

.designer-scope select {
  border: 1px solid var(--line, #d7e0e2);
  border-radius: 9px;
  min-height: 2.5rem;
  padding: 0.4rem 0.55rem;
}

.designer-scope :deep(.store-picker),
.designer-scope__warning {
  grid-column: 1 / -1;
}

.designer-scope__warning {
  background: #fffbeb;
  border-radius: 9px;
  color: #92400e;
  font-size: 0.78rem;
  margin: 0;
  padding: 0.65rem;
}

@media (max-width: 900px) {
  .designer-scope {
    grid-template-columns: 1fr;
  }
}

.report-designer {
  --line: rgba(122, 142, 146, 0.22);
  background: #e1e7e8;
  color: #172326;
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr) auto;
  height: calc(100vh - 5.5rem);
  min-height: 680px;
  overflow: hidden;
}

.designer-toolbar {
  align-items: center;
  background: #fff;
  border-bottom: 1px solid var(--line);
  display: flex;
  justify-content: space-between;
  min-height: 72px;
  padding: 0.65rem 1rem;
}

.designer-toolbar__identity,
.designer-toolbar__actions,
.panel-heading {
  align-items: center;
  display: flex;
  gap: 0.75rem;
}

.save-status {
  color: #68777a;
  font-size: 0.68rem;
  font-weight: 800;
}
.save-status.is-success { color: #087f5b; }
.save-status.is-danger { color: #b42318; }

.toolbar-label,
.panel-label {
  color: #6d7c80;
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0;
  margin: 0 0 0.15rem;
  text-transform: uppercase;
}

.report-name {
  background: transparent;
  border: 0;
  color: #172326;
  font-family: "Avenir Next", "Trebuchet MS", sans-serif;
  font-size: 1.15rem;
  font-weight: 800;
  min-width: 260px;
  outline: 0;
  padding: 0;
}

.designer-error {
  align-items: center;
  background: #fff1f0;
  border-bottom: 1px solid #fecaca;
  color: #991b1b;
  display: flex;
  gap: 0.75rem;
  padding: 0.7rem 1rem;
}

.designer-error p { margin: 0.15rem 0 0; }
.designer-error button { margin-left: auto; }

.designer-workspace {
  display: grid;
  grid-template-columns: 230px minmax(0, 1fr) 260px;
  min-height: 0;
}

.dataset-panel,
.properties-panel {
  background: #f8fafa;
  overflow-y: auto;
  padding: 1rem;
}

.dataset-panel { border-right: 1px solid var(--line); }
.properties-panel { border-left: 1px solid var(--line); }
.panel-heading { justify-content: space-between; margin-bottom: 0.85rem; }
.panel-heading h2,
.properties-panel h2 { font-size: 0.95rem; margin: 0; }
.empty-copy { color: #718083; font-size: 0.78rem; line-height: 1.5; }

.data-search {
  align-items: center;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 6px;
  color: #718083;
  display: flex;
  gap: 0.45rem;
  margin-bottom: 0.9rem;
  min-height: 38px;
  padding: 0 0.6rem;
}
.data-search:focus-within {
  border-color: #087f5b;
  box-shadow: 0 0 0 3px rgba(8, 127, 91, 0.1);
}
.data-search input {
  background: transparent;
  border: 0;
  color: #172326;
  font-size: 0.72rem;
  min-width: 0;
  outline: 0;
  width: 100%;
}

.dataset-group { margin-bottom: 1rem; }
.dataset-group__label {
  color: #7a888a;
  font-size: 0.62rem;
  font-weight: 900;
  margin: 0 0 0.3rem 0.35rem;
  text-transform: uppercase;
}

.dataset-option {
  align-items: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  color: inherit;
  display: flex;
  gap: 0.65rem;
  margin-bottom: 0.35rem;
  padding: 0.6rem;
  text-align: left;
  width: 100%;
}

.dataset-option:hover { background: #fff; border-color: var(--line); }
.dataset-option:disabled { cursor: default; opacity: 0.6; }
.dataset-option > :first-child { color: #087f5b; }
.dataset-option > span { display: grid; flex: 1; min-width: 0; }
.dataset-option strong { font-size: 0.8rem; }
.dataset-option small { color: #718083; font-size: 0.67rem; }

.data-configurator { display: grid; gap: 1rem; }
.data-configurator__summary,
.use-cases {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.data-configurator__summary span,
.use-cases span {
  background: #edf6f3;
  border: 1px solid #d0e7df;
  border-radius: 999px;
  color: #276557;
  font-size: 0.68rem;
  font-weight: 800;
  padding: 0.3rem 0.55rem;
}
.use-cases span { background: #f7f9f9; border-color: var(--line); color: #647477; }

.planned-fields-note {
  background: #fff8e8;
  border-left: 3px solid #d9a441;
  color: #6f5a2a;
  font-size: 0.76rem;
  font-weight: 700;
  margin: 0;
  padding: 0.55rem 0.7rem;
}

.query-editor {
  border-bottom: 1px solid var(--line);
  border-top: 1px solid var(--line);
  display: grid;
  gap: 0.85rem;
  padding: 0.9rem 0;
}

.query-editor__heading,
.query-section__head,
.query-toggle,
.aggregation-rule {
  align-items: center;
  display: flex;
  gap: 0.55rem;
  justify-content: space-between;
}

.query-editor__heading > div,
.query-toggle > span {
  display: grid;
}

.query-editor__heading span,
.query-section__head > span,
.query-toggle strong {
  color: #283b3d;
  font-size: 0.75rem;
  font-weight: 900;
}

.query-editor__heading small,
.query-toggle small,
.query-empty {
  color: #778689;
  font-size: 0.66rem;
  line-height: 1.4;
}

.query-section {
  display: grid;
  gap: 0.5rem;
}

.query-section + .query-section {
  border-top: 1px solid var(--line);
  padding-top: 0.75rem;
}

.query-section__head button,
.query-rule > button {
  align-items: center;
  background: transparent;
  border: 0;
  color: #087f5b;
  display: inline-flex;
  font-size: 0.68rem;
  font-weight: 850;
  gap: 0.2rem;
  padding: 0.2rem;
}

.query-rule > button { color: #9b4a43; }

.query-date-range,
.query-rule {
  display: grid;
  gap: 0.4rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.query-date-range label {
  display: grid;
  gap: 0.25rem;
}

.query-date-range span {
  color: #6b7a7d;
  font-size: 0.64rem;
  font-weight: 800;
}

.query-date-range input,
.query-rule input,
.query-rule select,
.query-period select,
.aggregation-rule select {
  background: #fff;
  border: 1px solid #ced8d9;
  border-radius: 5px;
  color: #26383a;
  font-size: 0.7rem;
  min-width: 0;
  padding: 0.42rem;
  width: 100%;
}

.query-rule {
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.9fr) 28px;
}

.query-rule input {
  grid-column: 1 / 3;
}

.query-rule input + input {
  grid-column: 1 / 3;
}

.query-rule--sort {
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.8fr) 28px;
}

.aggregation-list {
  display: grid;
  gap: 0.4rem;
}

.aggregation-rule span {
  color: #536568;
  font-size: 0.7rem;
  font-weight: 750;
}

.aggregation-rule select {
  width: 6.5rem;
}

.field-picker {
  border: 1px solid var(--line);
  border-radius: 7px;
  max-height: 330px;
  overflow-y: auto;
}
.field-choice {
  align-items: center;
  background: #fff;
  border-bottom: 1px solid var(--line);
  display: grid;
  gap: 0.7rem;
  grid-template-columns: auto minmax(0, 1fr) minmax(100px, auto);
  min-height: 54px;
  padding: 0.55rem 0.75rem;
}
.field-choice:last-child { border-bottom: 0; }
.field-choice input { accent-color: #087f5b; }
.field-choice__main { display: grid; gap: 0.12rem; }
.field-choice__main strong { color: #223234; font-size: 0.75rem; }
.field-choice__main small,
.field-choice__main em {
  color: #718083;
  font-size: 0.64rem;
  font-style: normal;
  line-height: 1.35;
}
.field-choice__sample {
  color: #526366;
  font-size: 0.68rem;
  max-width: 170px;
  overflow: hidden;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.field-choice.is-unavailable {
  background: #f2f4f4;
  color: #9aa4a6;
}
.field-choice.is-unavailable .field-choice__main strong,
.field-choice.is-unavailable .field-choice__main small,
.field-choice.is-unavailable .field-choice__sample { color: #929d9f; }
.field-choice.is-unavailable .field-choice__main em { color: #9b615c; }
.field-choice.is-manual { background: #fffdf5; }
.field-choice.is-manual .field-choice__main em { color: #8a6d1d; }
.field-choice.is-manual .field-choice__sample { color: #8a6d1d; font-style: italic; }

.configured-sample { min-width: 0; }
.configured-sample__scroll {
  border: 1px solid var(--line);
  border-radius: 7px;
  overflow-x: auto;
}
.configured-sample table { border-collapse: collapse; min-width: 100%; }
.configured-sample th,
.configured-sample td {
  border-right: 1px solid var(--line);
  font-size: 0.68rem;
  padding: 0.5rem 0.65rem;
  text-align: left;
  white-space: nowrap;
}
.configured-sample th { background: #263c3b; color: #fff; }
.configured-sample td { color: #526366; }

.sheet-stage {
  min-width: 0;
  overflow: auto;
  padding: 2rem;
}

.sheet-zoom-surface {
  min-height: 100%;
  transform-origin: top left;
  width: max-content;
}

.sheet-viewport {
  display: grid;
}

.sheet-corner,
.column-headings span,
.row-headings span {
  align-items: center;
  background: #edf1f1;
  color: #6a787b;
  display: flex;
  font-size: 0.66rem;
  justify-content: center;
}

.sheet-corner { border: 1px solid #ced7d8; }
.column-headings { display: grid; }
.column-headings span { border-right: 1px solid #ced7d8; border-top: 1px solid #ced7d8; }
.row-headings { display: grid; }
.row-headings span { border-bottom: 1px solid #ced7d8; border-left: 1px solid #ced7d8; }

.virtual-sheet {
  background-color: #fff;
  background-image:
    linear-gradient(to right, rgba(210, 219, 220, 0.62) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(210, 219, 220, 0.62) 1px, transparent 1px);
  background-size: 132px 30px;
  position: relative;
}

.report-table-block {
  background: #fff;
  border: 2px solid #087f5b;
  box-shadow: 0 8px 20px rgba(24, 51, 47, 0.12);
  overflow: hidden;
  position: absolute;
}

.report-table-block.is-selected {
  border-color: #f59f00;
  box-shadow: 0 0 0 3px rgba(245, 159, 0, 0.2), 0 10px 24px rgba(24, 51, 47, 0.15);
}

.report-table-block.has-collision {
  border-color: #c92a2a;
  box-shadow: 0 0 0 3px rgba(201, 42, 42, 0.16);
}

.report-table-block:focus-visible {
  outline: 3px solid rgba(8, 127, 91, 0.28);
  outline-offset: 3px;
}

.report-table-block__title {
  align-items: center;
  background: #e6f4ef;
  display: flex;
  gap: 0.45rem;
  min-height: 30px;
  padding: 0 0.55rem;
  touch-action: none;
  user-select: none;
}

.report-table-block__title:hover { cursor: grab; }
.report-table-block__title:active { cursor: grabbing; }

.report-table-block__title strong { font-size: 0.74rem; }
.report-table-block__title span { color: #5e716d; font-size: 0.62rem; margin-left: auto; }
.report-field-guide {
  background: #fff8e8;
  border-bottom: 1px solid #e8d7ad;
  color: #5f512f;
  display: grid;
  gap: 0.35rem;
  padding: 0.55rem 0.65rem;
}
.report-field-guide > strong {
  color: #725a20;
  font-size: 0.65rem;
  text-transform: uppercase;
}
.report-field-guide dl {
  display: grid;
  gap: 0.25rem;
  margin: 0;
}
.report-field-guide dl > div {
  display: grid;
  gap: 0.25rem;
  grid-template-columns: minmax(7rem, 0.32fr) minmax(12rem, 1fr);
}
.report-field-guide dt,
.report-field-guide dd {
  font-size: 0.63rem;
  line-height: 1.35;
  margin: 0;
}
.report-field-guide dt { font-weight: 900; }
.report-field-guide dd {
  display: grid;
  gap: 0.15rem;
}
.report-field-guide dd > span { display: block; }
.report-field-guide dd strong { color: #725a20; }
.report-table-block table { border-collapse: collapse; table-layout: fixed; width: 100%; }
.report-table-block th,
.report-table-block td {
  border-right: 1px solid #d9e1e1;
  height: 30px;
  overflow: hidden;
  padding: 0 0.5rem;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.report-table-block th { background: #263c3b; color: #fff; font-size: 0.66rem; }
.report-table-block td { color: #7b898b; font-size: 0.65rem; }
.report-table-block .empty-table-row {
  color: #718083;
  font-style: italic;
  text-align: center;
}
.preview-row-status {
  background: #f4f8f7;
  border-top: 1px solid #d9e1e1;
  color: #526b66;
  font-size: 0.62rem;
  font-weight: 800;
  padding: 0.35rem 0.5rem;
  text-align: right;
}

.sheet-empty {
  align-items: center;
  background: #fff;
  border: 1px dashed #8da09d;
  border-radius: 6px;
  color: #263c3b;
  display: grid;
  gap: 0.35rem;
  justify-items: center;
  left: 50%;
  padding: 1.25rem 2rem;
  position: absolute;
  top: 140px;
  transform: translateX(-50%);
}
.sheet-empty span { color: #70807e; font-size: 0.74rem; }

.properties-panel dl { display: grid; margin: 1rem 0 0; }
.properties-panel dl div {
  align-items: center;
  border-bottom: 1px solid var(--line);
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
}
.properties-panel dt { color: #718083; font-size: 0.72rem; }
.properties-panel dd { font-size: 0.72rem; font-weight: 800; margin: 0; }

.properties-heading,
.column-editor__heading,
.position-summary {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.icon-action {
  align-items: center;
  background: transparent;
  border: 1px solid var(--line);
  border-radius: 6px;
  color: #68777a;
  display: inline-flex;
  height: 32px;
  justify-content: center;
  width: 32px;
}
.icon-action--danger { color: #b42318; }
.icon-action:hover { background: #fff; }

.property-field {
  display: grid;
  gap: 0.35rem;
  margin-top: 1rem;
}
.property-field > span,
.column-editor__heading,
.position-summary > span {
  color: #718083;
  font-size: 0.7rem;
  font-weight: 800;
}
.property-field input,
.property-field select {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 6px;
  color: #172326;
  min-height: 38px;
  padding: 0 0.65rem;
  width: 100%;
}
.property-field input:focus,
.property-field select:focus {
  border-color: #087f5b;
  outline: 2px solid rgba(8, 127, 91, 0.12);
}

.placement-note,
.placement-error {
  font-size: 0.68rem;
  line-height: 1.4;
  margin: 0.45rem 0 0;
}
.placement-note { color: #68777a; }
.placement-error {
  background: #fff1f0;
  border-left: 3px solid #c92a2a;
  color: #8f201d;
  font-weight: 800;
  padding: 0.45rem 0.55rem;
}

.position-summary {
  border-bottom: 1px solid var(--line);
  margin-top: 0.8rem;
  padding: 0.7rem 0;
}
.position-summary strong { font-size: 0.72rem; }

.column-editor { margin-top: 1rem; }
.column-editor__heading { margin-bottom: 0.55rem; }
.add-manual-column {
  align-items: center;
  background: #fffdf5;
  border: 1px solid #eadba5;
  border-radius: 6px;
  color: #7b621c;
  display: inline-flex;
  font-size: 0.64rem;
  font-weight: 850;
  gap: 0.3rem;
  min-height: 30px;
  padding: 0 0.5rem;
}
.add-manual-column:disabled { opacity: 0.45; }
.column-option {
  align-items: center;
  border-bottom: 1px solid var(--line);
  display: flex;
  gap: 0.35rem;
  justify-content: space-between;
  min-height: 38px;
}
.column-option label {
  align-items: center;
  display: flex;
  font-size: 0.7rem;
  gap: 0.45rem;
  min-width: 0;
}
.column-option label span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.column-option input { accent-color: #087f5b; }
.column-actions { display: flex; }
.column-actions button {
  align-items: center;
  background: transparent;
  border: 0;
  color: #617174;
  display: inline-flex;
  height: 28px;
  justify-content: center;
  padding: 0;
  width: 24px;
}
.column-actions button:disabled { opacity: 0.25; }
.column-option--manual { background: #fffdf5; padding-left: 0.35rem; }
.manual-column-label {
  align-items: center;
  color: #8a6d1d;
  display: flex;
  gap: 0.4rem;
  min-width: 0;
}
.manual-column-label > span { display: grid; min-width: 0; }
.manual-column-label strong {
  color: #51451f;
  font-size: 0.68rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.manual-column-label small { color: #8a6d1d; font-size: 0.58rem; }

.manual-column-form { display: grid; gap: 0.9rem; }
.manual-column-form label { display: grid; gap: 0.35rem; }
.manual-column-form label > span {
  color: #263c3b;
  font-size: 0.74rem;
  font-weight: 900;
}
.manual-column-form input,
.manual-column-form select {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 7px;
  color: #172326;
  min-height: 42px;
  padding: 0 0.7rem;
}
.manual-column-form input:focus,
.manual-column-form select:focus {
  border-color: #087f5b;
  box-shadow: 0 0 0 3px rgba(8, 127, 91, 0.1);
  outline: 0;
}
.manual-column-error { color: #b42318; font-size: 0.68rem; margin: 0; }

.sheet-order-actions {
  display: grid;
  gap: 0.35rem;
  grid-template-columns: 1fr 1fr;
  margin-top: 0.65rem;
}
.sheet-order-actions button {
  align-items: center;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 6px;
  color: #526366;
  display: flex;
  font-size: 0.65rem;
  gap: 0.3rem;
  justify-content: center;
  min-height: 32px;
}
.sheet-order-actions button:disabled { opacity: 0.4; }
.selection-hint { color: #718083; font-size: 0.68rem; line-height: 1.5; margin-top: 1rem; }

.sheet-tabs {
  align-items: stretch;
  background: #f6f8f8;
  border-top: 1px solid var(--line);
  display: flex;
  min-height: 42px;
  padding-left: 230px;
}

.sheet-tabs button {
  background: transparent;
  border: 0;
  border-right: 1px solid var(--line);
  color: #5f7072;
  font-size: 0.72rem;
  font-weight: 800;
  padding: 0 1rem;
}
.sheet-tabs button.is-active {
  background: #fff;
  box-shadow: inset 0 -3px #087f5b;
  color: #143a32;
}
.sheet-tabs__add { width: 42px; }
.sheet-zoom-controls {
  align-items: center;
  border-left: 1px solid var(--line);
  display: flex;
  margin-left: auto;
}
.sheet-zoom-controls button {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  min-width: 38px;
  padding: 0 0.45rem;
}
.sheet-zoom-controls button:disabled { opacity: 0.35; }
.sheet-zoom-controls__value { min-width: 54px !important; }
.sheet-tabs__status { color: #829092; font-size: 0.66rem; margin: auto 0.8rem; }

.new-sheet-field {
  display: grid;
  gap: 0.45rem;
}
.new-sheet-field > span {
  color: #263c3b;
  font-size: 0.76rem;
  font-weight: 900;
}
.new-sheet-field input {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 7px;
  color: #172326;
  min-height: 42px;
  padding: 0 0.75rem;
}
.new-sheet-field input:focus {
  border-color: #087f5b;
  box-shadow: 0 0 0 3px rgba(8, 127, 91, 0.1);
  outline: 0;
}
.new-sheet-field small { color: #b42318; font-size: 0.68rem; }

@media (max-width: 1100px) {
  .designer-workspace { grid-template-columns: 210px minmax(0, 1fr); }
  .properties-panel { display: none; }
  .sheet-tabs { padding-left: 210px; }
}

@media (max-width: 700px) {
  .report-designer { height: auto; min-height: calc(100vh - 4rem); overflow: visible; }
  .designer-toolbar { align-items: flex-start; gap: 0.75rem; }
  .designer-toolbar__actions .ui-button { display: none; }
  .report-name { min-width: 0; width: 100%; }
  .designer-workspace { display: block; }
  .dataset-panel { border-bottom: 1px solid var(--line); border-right: 0; }
  .dataset-option { display: inline-flex; margin-right: 0.3rem; width: auto; }
  .dataset-option small { display: none; }
  .sheet-stage { min-height: 560px; padding: 1rem; }
  .sheet-tabs { padding-left: 0; position: sticky; bottom: 0; }
}
</style>